import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert, FormControlLabel } from "@mui/material";
import { FormikProps } from "formik";
import { useFeatureEntitlements } from "@frontegg/react";
import TransactionsType from "enums/TransactionsType";
import useCreateExpense from "hooks/useCreateExpense";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import useDuplicateExpenseCheck from "hooks/useDuplicateExpenseCheck";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import {
  CashExpense,
  MileageExpense,
} from "utils/reimbursements/reimbursementExpenseFormat";
import { Checkbox, Drawer, Select, toast } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import PersonalBankAccountDrawer from "components/common/users/PersonalBankAccountDrawer";
import CustomFields from "components/common/CustomFields";
import EmployeeSearchAutoComplete, {
  type Employee,
} from "components/common/EmployeeSearchAutoComplete";
import { SendUserBankAccountInviteModal } from "components/common/users/UserModules";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import DuplicateExpenseModal from "./DuplicateExpenseModal";
import ReimbursementSummary from "./ReimbursementSummary";
import CashReimbursementForm from "./CashReimbursementForm";
import MileageReimbursementForm from "./MileageReimbursementForm";
import AttachmentsSection from "./AttachmentsSection";
import BankAccountBanner from "./BankAccountBanner";
import DuplicateReviewBanner from "./DuplicateReviewBanner";
import ReimbursementFooter from "./ReimbursementFooter";
import { usePersonalBankAccountNudge } from "hooks/usePersonalBankAccountNudge";
import { useReimbursementAttachments } from "hooks/useReimbursementAttachments";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import {
  ReimbursementSteps,
  useReimbursementLifecycle,
} from "hooks/useReimbursementLifecycle";

interface ReimbursementDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
  /** An existing reimbursement to edit or resubmit. */
  expenseId?: string;
  /** Admin only: file on someone else's behalf. Everyone else always pays themselves. */
  choosePayee?: boolean;
}

/** Maximum number of receipt attachments allowed on a reimbursement. */
const MAX_RECEIPTS = 5;

/**
 * Submit or edit a reimbursement, on every route: details, then a summary.
 * `useCreateExpense` owns the form and the mutation; this orchestrates.
 */
const ReimbursementDrawer: React.FC<ReimbursementDrawerProps> = ({
  open,
  onClose,
  onSubmitted,
  onAfterClose,
  expenseId,
  choosePayee = false,
}) => {
  const { t } = useTranslation();
  const { user } = useGetLoggedInUser();

  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );

  const [expenseType, setExpenseType] = useState<TransactionsType | null>(null);

  const {
    formikCreateExpense,
    submitExpense,
    customFieldSettings,
    customFieldsInitialValues,
    resetFormValues,
    loading,
    loadingCustomFields,
  } = useCreateExpense({ expenseId, expenseType, suppressSubmitToasts: true });

  const { validateAndTouch, handleFormikHelperText, handleFormikError } =
    useFormikHelpers(formikCreateExpense);

  // ── Cross-cutting concerns, each in its own hook ────────────────────
  const { requestLeave, dialog } = useUnsavedChangesGuard(
    formikCreateExpense.dirty,
    onClose,
    { confirmLabel: t("Leave"), cancelLabel: t("Cancel"), tone: "danger" },
  );
  const lifecycle = useReimbursementLifecycle({
    open,
    editing: !!expenseId,
    payViewer: !choosePayee,
    customFieldDefaults: customFieldsInitialValues,
    user,
    formik: formikCreateExpense,
    resetFormValues,
    setExpenseType,
    requestLeave,
  });
  const { currentStep, setCurrentStep, handlePreviousStep } = lifecycle;

  const payToId = formikCreateExpense.values.payTo.id;
  const payingSelf = !payToId || payToId === user?.id;
  const bank = usePersonalBankAccountNudge(payToId, payingSelf);
  const [inviteOpen, setInviteOpen] = useState(false);

  const attachments = useReimbursementAttachments({
    formik: formikCreateExpense,
    maxReceipts: MAX_RECEIPTS,
  });

  // ── Duplicate-expense check (summary step only) ─────────────────────
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [hasReviewedDuplicates, setHasReviewedDuplicates] = useState(false);
  // The id outlives `open` so the review drawer keeps its content while it slides out.
  const [duplicateReview, setDuplicateReview] = useState({
    id: "",
    open: false,
  });

  const {
    potentialDuplicates,
    loading: loadingDuplicates,
    hasDuplicates,
  } = useDuplicateExpenseCheck({
    expenseValues: formikCreateExpense.values,
    enabled: currentStep === ReimbursementSteps.SUMMARY,
  });

  const duplicateIdsKey = potentialDuplicates
    .map((d) => d.candidate.id)
    .sort()
    .join("|");

  useEffect(() => {
    setHasReviewedDuplicates(false);
  }, [duplicateIdsKey, currentStep]);

  const submitBlockedByDuplicates = hasDuplicates && !hasReviewedDuplicates;

  const isSummary = currentStep === ReimbursementSteps.SUMMARY;
  const { expenseType: selectedType } = formikCreateExpense.values;

  // Someone else's bank account cannot be added for them, only asked for.
  const bankBanner = (
    <BankAccountBanner
      message={bank.message}
      onAdd={payingSelf ? bank.openDrawer : () => setInviteOpen(true)}
      actionLabel={payingSelf ? undefined : t("Invite")}
    />
  );
  const showSummaryTopSlot =
    !!bank.message || loadingDuplicates || hasDuplicates;

  return (
    <>
      <Drawer
        open={open}
        onClose={() => {
          if (loading) return;
          requestLeave();
        }}
        onAfterClose={() => {
          lifecycle.resetState();
          onAfterClose?.();
        }}
        loading={loading}
        title={expenseId ? t("Edit Reimbursement") : t("Submit Reimbursement")}
        footer={
          <ReimbursementFooter
            isSummary={isSummary}
            submitBlockedByDuplicates={submitBlockedByDuplicates}
            disabled={loading || loadingDuplicates || loadingCustomFields}
            onBack={handlePreviousStep}
            onNext={async () => {
              if (await validateAndTouch()) {
                setCurrentStep(ReimbursementSteps.SUMMARY);
              }
            }}
            onReview={() => setShowDuplicateModal(true)}
            onSubmit={async () => {
              // Show a single consolidated toast (rather than one for the expense
              // and another for its receipts). `submitExpense` reports the real
              // outcome, so on failure we keep the drawer open and preserve input.
              const { submitted, receipts, errorMessage } =
                await submitExpense();

              if (!submitted) {
                toast.error(
                  errorMessage || t("Failed to submit reimbursement"),
                );
                return;
              }

              if (receipts === "failed") {
                toast.warning(t("ReimbursementSubmittedReceiptFailed"));
              } else if (receipts === "uploaded") {
                toast.success(t("ReimbursementAndReceiptsSubmitted"));
              } else {
                toast.success(t("ReimbursementSubmitted"));
              }

              onSubmitted?.();
              onClose();
            }}
          />
        }
      >
        {currentStep === ReimbursementSteps.ENTER_DETAILS && (
          <Stack gap="var(--space-6)">
            {choosePayee && (
              <EmployeeSearchAutoComplete
                title={t("Pay to")}
                required
                employee={
                  payToId
                    ? (formikCreateExpense.values.payTo as Employee)
                    : null
                }
                handleSelectEmployee={(employee) =>
                  formikCreateExpense.setFieldValue("payTo", {
                    id: employee?.id || "",
                    firstName: employee?.firstName || "",
                    lastName: employee?.lastName || "",
                    email: employee?.email || "",
                  })
                }
                // handleBlur would mark the input's generated id, not payTo.id.
                handleBlur={() =>
                  formikCreateExpense.setFieldTouched("payTo.id", true)
                }
                errorText={
                  handleFormikHelperText("id", { parent: "payTo" }) || undefined
                }
              />
            )}

            {bankBanner}

            {/* `required` also hides the clear x, whose reset would wipe the whole form. */}
            <Select
              required
              disabled={!!expenseId}
              label={t("Select Expense Type")}
              placeholder={t("Select Expense Type")}
              value={selectedType || null}
              onChange={(value) => {
                formikCreateExpense.setFieldValue("expenseType", value ?? "");
                setExpenseType(value as TransactionsType | null);
                resetFormValues(value as TransactionsType | null);
              }}
              errorText={handleFormikHelperText("expenseType") || undefined}
              options={[
                {
                  id: TransactionsType.MILEAGE,
                  label: t("Mileage Reimbursement"),
                },
                {
                  id: TransactionsType.CASH,
                  label: t("Cash Reimbursement (Out-of-Pocket)"),
                },
              ]}
            />

            {selectedType && !formikCreateExpense.values.lostReceipt && (
              <AttachmentsSection
                attachments={formikCreateExpense.values.attachments}
                receiptRequired={attachments.receiptRequired}
                canUploadToOcr={attachments.canUploadToOcr}
                maxReceipts={MAX_RECEIPTS}
                expanded={attachments.isAccordionExpanded}
                onExpandedChange={attachments.setIsAccordionExpanded}
                onAddAssets={attachments.handleAddFiles}
                onRemove={attachments.handleRemove}
                onOcrResult={attachments.prefillFromOcr}
                errorText={
                  handleFormikError("attachments")
                    ? handleFormikHelperText("attachments")
                    : undefined
                }
              />
            )}

            {selectedType === TransactionsType.CASH &&
              formikCreateExpense.values.attachments.length === 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formikCreateExpense.values.lostReceipt || false}
                      onChange={attachments.handleLostReceiptChange}
                      name="lostReceipt"
                      color="primary"
                    />
                  }
                  label={t("Receipt missing")}
                />
              )}

            {attachments.ocrPrefilledFields.length > 0 && (
              <Alert
                severity="info"
                role="status"
                onClose={attachments.clearOcrPrefilled}
                sx={{
                  alignItems: "center",
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                <Trans
                  i18nKey="OcrPrefillBanner"
                  values={{
                    fields: attachments.ocrPrefilledFields.join(" & "),
                  }}
                />
              </Alert>
            )}

            {selectedType === TransactionsType.MILEAGE &&
              attachments.hasReceiptOrNotRequired && (
                <MileageReimbursementForm
                  formikCreateExpense={
                    formikCreateExpense as FormikProps<MileageExpense>
                  }
                />
              )}

            {selectedType === TransactionsType.CASH &&
              attachments.hasReceiptOrNotRequired && (
                <CashReimbursementForm
                  formikCreateExpense={
                    formikCreateExpense as FormikProps<CashExpense>
                  }
                />
              )}

            {featureCustomFields && selectedType && (payToId || expenseId) && (
              <CustomFields
                fields={customFieldSettings}
                values={formikCreateExpense.values.customFields ?? {}}
                onChange={(id, next) =>
                  formikCreateExpense.setFieldValue(`customFields.${id}`, next)
                }
                onBlur={(id) =>
                  formikCreateExpense.setFieldTouched(
                    `customFields.${id}`,
                    true,
                  )
                }
                errors={
                  formikCreateExpense.errors.customFields as
                    Record<string, string | string[] | undefined> | undefined
                }
                touched={
                  formikCreateExpense.touched.customFields as
                    Record<string, boolean | boolean[] | undefined> | undefined
                }
                variant="flat"
              />
            )}
          </Stack>
        )}

        {isSummary && (
          <ReimbursementSummary
            expenseData={formikCreateExpense.values}
            topSlot={
              showSummaryTopSlot ? (
                <Stack gap="var(--space-3)">
                  {bankBanner}
                  <DuplicateReviewBanner
                    loading={loadingDuplicates}
                    hasDuplicates={hasDuplicates}
                    reviewed={hasReviewedDuplicates}
                    onReview={() => setShowDuplicateModal(true)}
                  />
                </Stack>
              ) : undefined
            }
          />
        )}
      </Drawer>

      {/* Beside the drawer, not in its paper: the review drawer's fixed-position
        comments panel would otherwise stack under the drawer it belongs to. */}
      {dialog}

      <PersonalBankAccountDrawer
        open={bank.isDrawerOpen}
        handleCloseDrawer={bank.closeDrawer}
      />

      {choosePayee && (
        <SendUserBankAccountInviteModal
          openSendBankAccountInviteModal={inviteOpen}
          setOpenSendBankAccountInviteModal={setInviteOpen}
          userEmail={formikCreateExpense.values.payTo?.email}
          userId={payToId}
        />
      )}

      <DuplicateExpenseModal
        open={showDuplicateModal && !duplicateReview.open}
        duplicates={potentialDuplicates}
        onCancel={() => setShowDuplicateModal(false)}
        onEdit={() => {
          setShowDuplicateModal(false);
          setCurrentStep(ReimbursementSteps.ENTER_DETAILS);
        }}
        onAcknowledge={() => {
          setHasReviewedDuplicates(true);
          setShowDuplicateModal(false);
        }}
        onViewDetails={(id) => setDuplicateReview({ id, open: true })}
      />

      <TransactionDetailsDrawer
        open={duplicateReview.open}
        transactionId={duplicateReview.id}
        handleCloseDrawer={() =>
          setDuplicateReview((review) => ({ ...review, open: false }))
        }
        drawerTitle={t("Expense Review")}
      />
    </>
  );
};

export default ReimbursementDrawer;
